import { Link } from "react-router-dom";
import { ArrowRight, UserCheck, ListChecks, BarChart3, Flag } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CTABanner from "../components/CTABanner";
import Mandala from "../components/Mandala";
import { Reveal } from "../components/motion";
import useMeta from "../lib/useMeta";
import { breadcrumb, serviceSchema } from "../lib/seo";

const PILLARS = [
  { icon: UserCheck, title: "One SPOC", text: "A single point of contact who knows your requirements, vendors and history." },
  { icon: ListChecks, title: "One Tracker", text: "Every requirement, quotation, order and issue in one shared view." },
  { icon: BarChart3, title: "One Monthly MIS", text: "Spend, orders, open issues and vendor performance in one monthly review." },
  { icon: Flag, title: "One Owner till Closure", text: "Nothing is 'with the vendor'. A Saarthi owns every requirement till it closes." },
];

const TRACKER_ROWS = [
  { req: "Ergonomic chairs × 40", cat: "Furniture & CAPEX", vendor: "Vendor A", status: "Delivered", eta: "Closed" },
  { req: "Housekeeping chemicals (monthly)", cat: "Housekeeping", vendor: "Vendor B", status: "In transit", eta: "24 Sep" },
  { req: "Pantry restock — 2 floors", cat: "Pantry & Beverages", vendor: "Vendor C", status: "Ordered", eta: "26 Sep" },
  { req: "AC servicing — HQ", cat: "Electrical & MRO", vendor: "Vendor D", status: "Scheduled", eta: "27 Sep" },
  { req: "Welcome kits × 60", cat: "Gifting & Uniforms", vendor: "Vendor A", status: "Issue raised", eta: "Fix by 25 Sep" },
];

const STATUS_STYLES = {
  Delivered: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  "In transit": "bg-steel/15 text-blue-300 border-steel/40",
  Ordered: "bg-white/10 text-slate-300 border-white/15",
  Scheduled: "bg-white/10 text-slate-300 border-white/15",
  "Issue raised": "bg-saffron/15 text-saffron border-saffron/40",
};

const STEPS = [
  { num: "01", title: "Send it", text: "Any requirement — item, vendor problem or site task — to your Saarthi." },
  { num: "02", title: "We run it", text: "Sourcing, quotations, vendor coordination and delivery tracking." },
  { num: "03", title: "You track it", text: "One shared tracker for every order, issue and open action." },
  { num: "04", title: "We close & report", text: "Closure with documentation, rolled into your monthly MIS." },
];

const Bar = ({ label, pct }) => (
  <div>
    <div className="text-[11px] text-slate-300 mb-1.5">{label}</div>
    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full rounded-full bg-saffron" style={{ width: `${pct}%` }} />
    </div>
  </div>
);

export default function OneDesk() {
  useMeta({
    title: "Saarthva OneDesk | Procurement & Vendor Management",
    description: "One point of contact for sourcing, vendor coordination, delivery tracking, issue closure and procurement reporting. Stop chasing vendors.",
    path: "/onedesk",
    schemas: [
      breadcrumb("Saarthva OneDesk", "/onedesk"),
      serviceSchema({
        name: "Saarthva OneDesk — Procurement & Vendor Management",
        description: "One point of contact for sourcing, vendor coordination, delivery tracking, issue closure and procurement reporting. Stop chasing vendors.",
        path: "/onedesk",
      }),
    ],
  });
  return (
    <div data-testid="onedesk-page">
      <PageHeader
        eyebrow="SAARTHVA ONEDESK"
        title="Stop chasing vendors."
        accent="Send it to Saarthva."
        description="One point of contact for sourcing, quotations, vendor coordination, delivery tracking, issue closure and monthly reporting."
      />

      <section className="bg-mist" data-testid="onedesk-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.07}>
                  <div className="h-full bg-white rounded-2xl border border-line p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200 hover:border-saffron/40" data-testid={`onedesk-pillar-${i}`}>
                    <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center mb-5">
                      <Icon className="w-5 h-5 text-saffron" strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-navy">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white grain relative overflow-hidden" data-testid="onedesk-tracker">
        <Mandala className="absolute -left-40 -bottom-48 w-[520px] h-[520px] text-steel opacity-40 spin-slow" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-4">
            <p className="font-mono text-[11px] tracking-[0.3em] text-saffron mb-4">THE TRACKER</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight max-w-2xl">
              Every requirement. One view. Zero follow-up anxiety.
            </h2>
          </Reveal>
          <p className="font-mono text-[10px] tracking-[0.3em] text-slate-500 mb-8" data-testid="tracker-sample-label">
            SAMPLE TRACKER VIEW — ILLUSTRATIVE FORMAT, NOT LIVE DATA
          </p>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur overflow-hidden" data-testid="tracker-table">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-slate-500">
                <span className="col-span-4">REQUIREMENT</span>
                <span className="col-span-3">CATEGORY</span>
                <span className="col-span-2">STATUS</span>
                <span className="col-span-3">ETA / CLOSURE</span>
              </div>
              {TRACKER_ROWS.map((r, i) => (
                <div key={i} className="grid md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-white/5 last:border-b-0 items-center" data-testid={`tracker-row-${i}`}>
                  <p className="md:col-span-4 font-semibold text-sm text-white">{r.req}</p>
                  <p className="md:col-span-3 text-xs text-slate-400">{r.cat}</p>
                  <p className="md:col-span-2">
                    <span className={`inline-block rounded-full border text-[11px] font-semibold px-3 py-1 ${STATUS_STYLES[r.status]}`}>
                      {r.status}
                    </span>
                  </p>
                  <p className="md:col-span-3 text-xs text-slate-400">{r.eta}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            <Reveal><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="onedesk-mis-spend">
              <p className="text-xs font-semibold text-slate-300 mb-4">Spend by Category</p>
              <div className="space-y-3">
                <Bar label="Housekeeping" pct={66} /><Bar label="Pantry" pct={52} /><Bar label="Furniture" pct={80} />
              </div>
            </div></Reveal>
            <Reveal delay={0.05}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="onedesk-mis-orders">
              <p className="text-xs font-semibold text-slate-300 mb-4">Orders Open / Closed</p>
              <div className="flex items-end gap-6">
                <div><p className="font-display text-3xl font-bold text-saffron">8</p><p className="text-[11px] text-slate-400 mt-1">Open</p></div>
                <div><p className="font-display text-3xl font-bold text-white">64</p><p className="text-[11px] text-slate-400 mt-1">Closed</p></div>
              </div>
            </div></Reveal>
            <Reveal delay={0.1}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="onedesk-mis-issues">
              <p className="text-xs font-semibold text-slate-300 mb-4">Issues by Age</p>
              <div className="space-y-3">
                <Bar label="< 48 hours" pct={70} /><Bar label="This week" pct={45} /><Bar label="Escalated" pct={12} />
              </div>
            </div></Reveal>
            <Reveal delay={0.15}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="onedesk-mis-vendor">
              <p className="text-xs font-semibold text-slate-300 mb-4">Vendor Scorecard</p>
              <div className="space-y-3">
                <Bar label="Quality" pct={84} /><Bar label="Timelines" pct={72} /><Bar label="Responsiveness" pct={90} />
              </div>
            </div></Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="onedesk-how">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">HOW ONEDESK WORKS</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">Send. Run. Track. Close.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.08}>
                <div className="border-t-2 border-saffron/60 pt-5" data-testid={`onedesk-step-${step.num}`}>
                  <p className="font-mono text-saffron text-sm tracking-widest">{step.num}</p>
                  <h3 className="mt-3 font-display text-xl font-bold text-navy">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 rounded-2xl bg-cream border border-saffron/30 p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="onedesk-start">
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-navy">You don't need a contract to start.</h3>
                <p className="mt-2 text-slate-600 max-w-xl">
                  Send one requirement. If we handle it well, scale to a category, a site or your full operations.
                  OneDesk works alongside <Link to="/managed-procurement" className="font-semibold text-saffron hover:text-navy transition-colors">Managed Procurement</Link> and <Link to="/workplace-operations" className="font-semibold text-saffron hover:text-navy transition-colors">Workplace Operations</Link>.
                </p>
              </div>
              <Link
                to="/request-quote"
                data-testid="onedesk-start-cta"
                className="inline-flex items-center gap-2 rounded-full bg-saffron hover:bg-saffrondark text-white font-semibold px-7 py-3.5 transition-colors shrink-0"
              >
                Start with one requirement <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title="Your desk is"
        accent="one message away."
        copy="Send a requirement to Saarthva OneDesk — a Saarthi owns it till closure."
      />
    </div>
  );
}
